/* ----------------- React Native Paper --------------- */
import { List, Text } from "react-native-paper";

/* ----------------- Memorang Types --------------- */

import { skillIconMap } from "@constants/common";
import { events } from "@constants/tracking";
import Icon from "@expo/vector-icons/MaterialCommunityIcons";

import type { Skill } from "@features/report/helpers/point-normalization";
import {
	getCompositeScoreAndLevel,
	skillCutoffs,
} from "@features/report/helpers/point-normalization";
import { getNumAttempted } from "@helpers/content/tag";
import { isWeb } from "@helpers/platform";
import { useAppStore } from "@hooks/useAppStore";
import { useAppTheme } from "@hooks/useAppTheme";
import { useDevice } from "@hooks/useDevice";
import { smartPluralize } from "@memorang/helpers/common";
import type { Distribution } from "@memorang/types";
import type { Item, MixedItemTag } from "@memorang/types";
import Box from "@memorang/ui/components/Box";
import CardContainer from "@memorang/ui/components/CardContainer";
import { CustomAppBar } from "@memorang/ui/components/CustomAppbar";
import CustomChip from "@memorang/ui/components/CustomChip";
import DonutChart from "@memorang/ui/components/DonutChart";
import type { IconType } from "@memorang/ui/icon";
/* ----------------- Local Components --------------- */
import { trackCustomEvent } from "analytics";
import CustomMarkdownRenderer from "components/CustomMarkdownRenderer";
import InfoAlert from "components/InfoAlert";
import { Link, router } from "expo-router";
import { useTranslation } from "react-i18next";
import type { StyleProp, ViewStyle } from "react-native";

import ScrollContainer from "@memorang/ui/components/ScrollContainer";
type Props = {
	title: string;
	tags: MixedItemTag[];
};

const getSkillKnowMoreUrl = (skill: string) => {
	return `https://www.ets.org/toefl/test-takers/ibt/about/content/${skill.toLowerCase()}.html`;
};

type GroupedItems = {
	[variant: string]: MixedItemTag;
};

const ProficiencyChildListItem = ({
	distKey,
	title,
	icon,
	iconColor,
	description,
	count,
}: {
	distKey: string;
	title: string;
	icon: IconType;
	iconColor: string;
	description: string;
	count: number;
}) => {
	const theme = useAppTheme();
	return (
		<List.Item
			key={distKey}
			titleStyle={{
				fontSize: 14,
			}}
			title={title}
			left={(p) => <List.Icon {...p} icon={icon} color={iconColor} />}
			descriptionNumberOfLines={3}
			description={<CustomMarkdownRenderer fontSize={12} text={description} />}
			descriptionStyle={{
				color: theme.colors.onSurfaceVariant,
				fontSize: 12,
			}}
			style={{
				paddingLeft: 54,
			}}
			right={(p) => (
				<List.Icon
					{...p}
					icon={() => (
						<CustomChip>
							{`${count} ${smartPluralize("item", count)}`}
						</CustomChip>
					)}
				/>
			)}
		/>
	);
};

export const PointsBasedTagsDetailView = ({ title, tags }: Props) => {
	const { t } = useTranslation();

	const theme = useAppTheme();

	const { isMobile } = useDevice();

	const numItemsAttempted = tags.reduce(
		(acc, tag) => acc + getNumAttempted(tag.distribution),
		0,
	);

	const sortedMasteryLevels = useAppStore
		.getState()
		.app.masteryLevels!.sort((a, b) => a.sortOrder - b.sortOrder);

	const mappedTags = tags.map((tag) => {
		const groupedByVariant = tag.items?.reduce(
			(acc: GroupedItems, item: Item) => {
				if (!item.variant) {
					return acc;
				}
				if (!acc[item.variant]) {
					acc[item.variant] = {
						type: item.variant,
						numItems: tag.numItems,
						title: t(`session.itemTypes.${item.variant}`),
						distribution: sortedMasteryLevels
							.map((level) => level.id)
							.reduce<Record<string, number>>((acc, level) => {
								acc[level] = 0;
								return acc;
							}, {}),
						items: [],
						id: item.id,
					};
				}
				acc[item.variant]!.items?.push(item);

				return acc;
			},
			{},
		);
		const score = Math.round(tag.averageScore || 0);

		return {
			...tag,
			title: t(`dashboard.skills.${tag.label || tag.title}`) || "",
			distribution: tag.distribution,
			score,
			children: groupedByVariant ? Object.values(groupedByVariant) : [],
		};
	});

	const renderLeftComp = ({
		p,
		distribution,
		innerRadius = 12,
		outerRadius = 22,
		score,
		totalScore,
		icon,
		masteryLevel,
	}: {
		p: {
			color: string;
			style: StyleProp<ViewStyle>;
		};
		distribution: Distribution;
		innerRadius?: number;
		outerRadius?: number;
		score?: number;
		totalScore: number;
		icon?: IconType;
		masteryLevel?: (typeof sortedMasteryLevels)[number];
	}) => {
		const colorScale = [masteryLevel?.lightColor, theme.colors.progress.none];
		return (
			<Box {...p} justifyContent="center">
				<DonutChart
					distribution={
						score != null ? [score, totalScore - score] : distribution
					}
					innerRadius={innerRadius}
					radius={outerRadius}
					colorScale={colorScale}
					score={score}
					variant={score != null ? "progress" : "mastery"}
				>
					{icon && (
						<Icon name={icon} size={18} color={theme.colors.onSurfaceVariant} />
					)}
				</DonutChart>
			</Box>
		);
	};

	const ListSection = (propsListSection: MixedItemTag) => {
		const { title, distribution, children } = propsListSection;

		const hasChildTags = children && children.length > 0;

		const trademark = useAppStore((state) => state.app.trademark);
		const numAttempts = getNumAttempted(distribution);
		const numItems = propsListSection.numItems || 0;

		const numNotAttempted = propsListSection.distribution.none;
		const distKeys = Object.keys(distribution).filter((key) => key !== "none");
		const finalDistKeys = [...distKeys, "none"];

		const { skillDetails } = getCompositeScoreAndLevel(
			[
				{
					skill: propsListSection.label as Skill,
					numPointsEarned: propsListSection.numPointsEarned || 0,
					numTotalPoints: propsListSection.numTotalPoints || 0,
				},
			],
			t,
		);
		const [firstSkill] = skillDetails;
		const masteryLevel = sortedMasteryLevels.find(
			(item) => item.id === firstSkill.level,
		);
		const description = `${t("common.estimatedProficiency")} - ${t(masteryLevel?.alias.title || "")}`;
		const descriptionAttempted = `${numAttempts} ${t("common.of")} ${numItems} ${smartPluralize("item", numItems)} ${t("common.attempted")}`;

		if (hasChildTags) {
			return (
				<List.Accordion
					title={title}
					descriptionNumberOfLines={3}
					style={{
						backgroundColor: theme.colors.elevation.level1,
					}}
					description={
						isMobile ? (
							<Box paddingTop={4}>
								<Text variant="bodySmall">{description}</Text>
								<Text
									style={{
										color: theme.colors.onSurfaceVariant,
										lineHeight: 20,
									}}
									variant="labelSmall"
								>
									{descriptionAttempted}
								</Text>
							</Box>
						) : (
							description
						)
					}
					titleStyle={{
						fontSize: 14,
					}}
					descriptionStyle={{
						color: theme.colors.onSurfaceVariant,
						fontSize: 12,
					}}
					left={(p) =>
						renderLeftComp({
							p,
							distribution,
							score: numAttempts,
							totalScore: numItems || 0,
							innerRadius: 18,
							outerRadius: 20,
							icon: skillIconMap[title],
							masteryLevel,
						})
					}
					onPress={() => {
						trackCustomEvent({
							eventName: events.skillExpanded,
							skill: title,
						});
					}}
					titleNumberOfLines={3}
					right={({ isExpanded }) => (
						<Box flexDirection="row" gap={8} alignItems="center">
							{!isMobile && (
								<Text
									style={{
										color: theme.colors.onSurfaceVariant,
										lineHeight: 24,
									}}
									variant="labelSmall"
								>
									{descriptionAttempted}
								</Text>
							)}
							<List.Icon
								icon={isExpanded ? "chevron-up" : "chevron-down"}
								color={theme.colors.onSurfaceVariant}
							/>
						</Box>
					)}
				>
					{finalDistKeys.map((item) => {
						if (item === "none") {
							return (
								<ProficiencyChildListItem
									key={item}
									distKey={item}
									title={t("common.notAnswered")}
									description={t("common.didNotAnswer")}
									count={numNotAttempted}
									icon={"speedometer-slow" as const}
									iconColor={theme.colors.onSurfaceVariant}
								/>
							);
						}

						const masteryLevel = sortedMasteryLevels.find(
							(level) => level.id === item,
						);
						const skillCutoff =
							skillCutoffs[propsListSection.label!][
								item as keyof (typeof skillCutoffs)[string]
							];

						if (!skillCutoff) {
							return null;
						}

						return (
							<ProficiencyChildListItem
								key={item}
								distKey={item}
								title={`${t(`session.masteryLevels.${item}`)} ${t("common.proficiency")}`}
								description={t("session.masteryLevels.proficiencySubtitle", {
									trademark,
									proficiency: t(
										`session.masteryLevels.${item}`,
									)?.toLowerCase(),
									minScore: skillCutoff.min,
									maxScore: skillCutoff.max,
									skill: title.toLowerCase(),
								})}
								count={distribution[item]}
								icon={masteryLevel?.icon as IconType}
								iconColor={masteryLevel?.lightColor!}
							/>
						);
					})}

					<Box
						paddingTop={16}
						paddingBottom={8}
						borderBottomWidth={1}
						paddingLeft={8}
						paddingRight={8}
						borderBottomColor={theme.colors.divider}
					>
						<InfoAlert
							text={t(
								`session.masteryLevels.infoSectionText.${propsListSection.label}`,
								{
									trademark,
								},
							)}
							fontSize={11}
							bottomComponent={
								isWeb && (
									<Link
										href={
											getSkillKnowMoreUrl(propsListSection.label || "") as never
										}
										style={{
											paddingTop: 16,
										}}
										target="_blank"
									>
										<Text
											variant="labelMedium"
											style={{
												color: theme.colors.primary,
											}}
										>
											{t("common.knowMore")}
										</Text>
									</Link>
								)
							}
							alignItems="flex-start"
							backgroundColor={theme.palette.info.main}
							title={t("session.masteryLevels.infoSectionLabel", {
								skill: title,
							})}
						/>
					</Box>
				</List.Accordion>
			);
		}
		return (
			<>
				<List.Item
					title={title}
					left={(p) => (
						<List.Icon
							{...p}
							icon="speedometer-low"
							color={theme.colors.onSurfaceVariant}
						/>
					)}
					right={(p) => (
						<List.Icon
							{...p}
							icon={() => <CustomChip>{numNotAttempted}</CustomChip>}
							color={theme.colors.onSurfaceVariant}
						/>
					)}
				/>
			</>
		);
	};

	const subtitle = `${t("progress.tagProgress.subtitle", {
		numItems: numItemsAttempted,
		type: smartPluralize("skill", tags.length),
		count: tags.length,
		item: smartPluralize("item", numItemsAttempted),
	})}`;

	// const getMockedDist = (skill: string, distribution: Distribution) => {
	// 	if (skill === "Reading" || skill === "Listening") {
	// 		return {
	// 			...distribution,
	// 			belowLowIntermediate: 0,
	// 		};
	// 	}
	// 	return {
	// 		...distribution,
	// 		belowBasic: 0,
	// 	};
	// };
	return (
		<>
			<CustomAppBar
				title={`${t("common.performance")} ${t("common.details")}`}
				leftIconType="back"
				onPressBack={() => {
					if (router.canGoBack()) {
						router.back();
					} else {
						router.push("/(protected)/(tabs)/progress");
					}
				}}
				hideCommandBar
			/>
			<ScrollContainer
				maxWidth="md"
				contentContainerStyle={{
					paddingBottom: 100,
				}}
			>
				{isMobile ? (
					<CardContainer
						title={title}
						subtitle={subtitle}
						icon={"tag-multiple-outline"}
					>
						{mappedTags.map((item) => (
							<ListSection key={item.id} {...item} />
						))}
					</CardContainer>
				) : (
					<Box padding={24}>
						<CardContainer
							title={title}
							subtitle={subtitle}
							icon={"tag-multiple-outline"}
						>
							<Box>
								{mappedTags.map((item) => (
									<ListSection
										key={item.id}
										{...item}
										// distribution={getMockedDist(item.label!, item.distribution)}
									/>
								))}
							</Box>
						</CardContainer>
					</Box>
				)}
			</ScrollContainer>
		</>
	);
};
